import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ControlContainer, NgForm } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { take, takeUntil } from 'rxjs/operators';
import { ConfiguredToastrService } from '../../../../../helper/toastr/configured-toastr.service';
import { DeactivationDialogService } from '../../../../../helper/can-deactivate/deactivation-dialog.service';
import { ConfirmationDialogComponent } from '../../../../../helper/confirmation-dialog-component/confirmation-dialog.component';
import { EditColumnDialogComponent } from '../../checklist-modify/edit-column-dialog/edit-column-dialog.component';
import { AnessaTranslationService } from '../../../../../helper/translation/anessa-translation.service';
import { ProjectChecklistService } from '../../checklist.service';
import {
  ChecklistCellDataItem,
  ProjectChecklist,
  ProjectChecklistColumn,
  ProjectChecklistRow
} from '../../../../../models/project/project-info.model';
import Utils from '../../../../../util';
import { EditRowDialogComponent } from '../../checklist-modify/edit-row-dialog/edit-row-dialog.component';
import { ProjectCommunicationService } from '../../../../../communication/project-comms.service';
import { SectionType } from '../../../../../models/enums/checklist/checklist-section-type.enum';
import { DropdownDisplayItem } from '../../../../../models/data-structures/dropdown-display-item';
import { EditCellDialogComponent, EditCellDialogResponse } from '../../checklist-modify/edit-cell-dialog/edit-cell-dialog.component';
import { v4 as uuid } from 'uuid';
import { UserpilotReloader } from '../../../../../helper/userpilot-reloader.component';
import { SectionFormService } from '../checklist-section-form.service';

@Component({
  selector: 'app-project-info-checklist-section',
  templateUrl: './checklist-section.component.html',
  styleUrls: [
    './checklist-section.component.scss',
  ],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class ProjectInfoChecklistSectionComponent extends UserpilotReloader implements OnInit, OnDestroy {
  @Output() saved = new EventEmitter<ProjectChecklist>();
  @Output() cancel = new EventEmitter<boolean>();
  @Output() saveSectionNotifier = new EventEmitter<{ row, columns }>();
  @Output() addSectionNotifier = new EventEmitter<{ row, columns }>();
  @Output() removeSectionNotifier = new EventEmitter<ProjectChecklistColumn[]>();
  @Input() editInProgress: boolean = false;
  @Output() editInProgressChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  onDestroyNotifier = new EventEmitter<any>();

  @Input() sectionTitle: string;
  @Input() isFirstFeedstockSection: boolean;
  @Input() isTabStyle: boolean = false;

  @Input() rowIndex: number = null;
  @Input() checklistIndex: number = null;
  rowColumns: ProjectChecklistColumn[] = null;
  @Input() set checklistColumnsInput(cols: ProjectChecklistColumn[]) {
    this.rowColumns = [...cols];
  }
  checklistRow: ProjectChecklistRow = null;
  @Input() set checklistRowInput(rowInput: ProjectChecklistRow) {
    if (rowInput) {
      this.checklistRow = rowInput;
      this.maxLevelShown = this.getMaxLevel();
      this.prepareTableData();
    }
  }
  @Output() cancelNotifier = new EventEmitter<{ row, columns, timeout }>();
  originalRow: ProjectChecklistRow;
  originalCols: ProjectChecklistColumn[];
  // checklist: ProjectChecklist;

  dataSource = new MatTableDataSource<ProjectChecklistRow>();
  editMode = false;

  displayedColumns: string[] = [];
  displayedControls: string[] = [];
  columnNames: string[] = [];
  controlNames: string[] = [];

  editColumn: string;
  editRow: number;
  maxLevelShown: number = -1;

  pageDirty = false;

  showTable = false;
  showClearName = false;

  trigger: string;
  isProjectInput = false;
  isInitialUser = false;
  readOnlyInitialChecklists: boolean;
  editOnlyInitialChecklists: boolean;

  SectionType = SectionType;
  sectionTypes: DropdownDisplayItem[] = [];

  columnLockSettings = {};

  constructor(
    private dialog: MatDialog,
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private checklistService: ProjectChecklistService,
    private sectionFormService: SectionFormService,
    private deactivationDialogService: DeactivationDialogService,
    private toastrService: ConfiguredToastrService,
    private translationService: AnessaTranslationService,
    private projectCommService: ProjectCommunicationService,
  ) {
    super();
  }

  ngOnInit() {
    this.isInitialUser = Utils.isInitialFormUser();
    if (this.router.url.includes('project-input')) {
      this.isProjectInput = true;
    }
    this.readOnlyInitialChecklists = Utils.readOnlyInitialChecklists();
    this.editOnlyInitialChecklists = Utils.editOnlyInitialChecklists();

    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier.asObservable()))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
      });

    this.sectionFormService.cancelEvent$
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        if (this.pageDirty) {
          this.deactivationDialogService.confirm(this.translationService.get('PROJECT.COMMON.DEACTIVATE_CONFIRM'))
          .pipe(take(1)).subscribe(val => {
            if (val) {
              this.onCancel(500);
              this.sectionFormService.allowLeave(true);
            } else {
              this.sectionFormService.allowLeave(false);
            }
          });
        } else {
          this.sectionFormService.allowLeave(true);
        }
      });

    // this.projectCommService.projectInputDraftSaveNotifier
    // .pipe(takeUntil(this.onDestroyNotifier.asObservable()))
    // .subscribe(save => {
    //   if (save === true && this.checklistForm) {
    //     try {
    //       if (this.checklistForm.valid) {
    //         this.onSave();
    //       } else {
    //         this.toastrService.error(this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.TEMPLATE.ERROR_EMPTY_NAME'));
    //         this.projectCommService.projectInputDraftSaveNotifier.next(false);
    //       }
    //     } catch (error) {
    //       this.toastrService.error(error.message);
    //       this.projectCommService.projectInputDraftSaveNotifier.next(false);
    //     }
    //   }
    // });
    this.setSectionTypes();
  }

  setSectionTypes() {
    const BASE_PATH = 'PROJECT.PROJECT_INFO.CHECKLIST';
    const temp = [
      new DropdownDisplayItem(BASE_PATH + '.SECTION_TYPES.GENERAL', SectionType.GENERAL),
      new DropdownDisplayItem(BASE_PATH + '.SECTION_TYPES.FEEDSTOCK', SectionType.FEEDSTOCK),
      new DropdownDisplayItem(BASE_PATH + '.SECTION_TYPES.MEMBERS', SectionType.MEMBERS),
      new DropdownDisplayItem(BASE_PATH + '.SECTION_TYPES.PLANTSPEC', SectionType.PLANTSPEC),
      new DropdownDisplayItem(BASE_PATH + '.SECTION_TYPES.FINANCIAL', SectionType.FINANCIAL)
    ];
    this.sectionTypes = temp;
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit(true);
    this.onDestroyNotifier.unsubscribe();
  }

  hideSaveButton() {
    return Utils.hasInputListAccess() && !Utils.hasFormAccess();
  }

  markFormAsDirty() {
    this.pageDirty = true;
  }

  canDeactivate() {
    if (this.pageDirty) {
      return this.deactivationDialogService.confirm(this.translationService.get('PROJECT.COMMON.DEACTIVATE_CONFIRM'));
    }
    return true;
  }

  /**
   * Recursive depth-first search to extract nested children to a single level list
   * @param rows list of rows to extract children from
   * @param parentIndex index of parent used to create dot id from
   * @returns a single level list containing all rows
   */
  private flattenChecklistRows(rows, parentIndex?: string) {
    const flattenedRows = [];
    rows.forEach((row, index) => {
      let dotIndex: string;
      if (parentIndex) {
        dotIndex = `${parentIndex}.${String(index + 1)}`;
      } else {
        dotIndex = String(index + 1);
      }
      row.data.id = dotIndex;
      flattenedRows.push(row);
      // Only include rows up to the overall selected level
      if (row.level < this.maxLevelShown) {
        flattenedRows.push(...this.flattenChecklistRows(row.children, dotIndex));
      }
    });
    return flattenedRows;
  }

  private prepareTableData() {
    this.showTable = false;
    // Update max shown level if the max level is lower than the selected level
    if (this.maxLevelShown > this.getMaxLevel()) {
      this.maxLevelShown = this.getMaxLevel();
    }
    this.columnNames = this.rowColumns.map(column => column.name);
    this.controlNames = this.columnNames.map(name => `control${name}`);
    this.loadColumnSettings();
    if (this.editMode) {
      this.displayedColumns = ['addControl', ...this.columnNames, 'removeControl'];
    } else {
      this.displayedColumns = [...this.columnNames];
    }
    this.displayedControls = ['intEmptyAdd', ...this.controlNames, 'intEmptyRemove'];
    if (this.checklistRow.children.length > 0) {
      // Only show level control and id columns if there is at least one row
      // if (this.editMode) {
      //   this.displayedColumns.splice(1, 0, 'levelControl');
      // } else {
      //   this.displayedColumns.splice(1, 0);
      // }
      // this.displayedControls.splice(1, 0, 'intEmptyLevel');
      const tableData = this.flattenChecklistRows(this.checklistRow.children);
      this.dataSource.data = tableData;
    } else {
      this.dataSource.data = [];
    }
    this.showTable = true;
  }

  edit(row: number, col: string) {
    // check if row is locked before enabling the edit
    if (col) {
      const column = this.checklistRow.columnSettings.find(c => c.name === col);
      if (column && column.locked || this.flattenChecklistRows(this.checklistRow.children)[row].locked || !this.hasEditAccess() || !column && this.rowColumns.find(c => c.name === col).locked) {
        return;
      }
    }
    this.editRow = row;
    this.editColumn = col;
  }

  isEdit(row: number, col: string) {
    if (col === 'id') {
      return false;
    }
    return (!this.editMode && !this.columnLockSettings[col]) || this.editRow === row && this.editColumn === col;
  }

  rowLocked(row: number) {
    return this.flattenChecklistRows(this.checklistRow.children)[row].locked;
  }

  /**
   * Determines if a string is a hex code
   * @param value string value to check if hex code
   * @returns true or false depending on if the string contains a hex code
   */
  isColourHexCode(value: string) {
    if (value != null && String(value).startsWith('#')) {
      return true;
    }
    return false;
  }

  getRowStyle(row: ProjectChecklistRow, colIndex) {
    if (row.level === 1 || colIndex === 0 || colIndex === undefined) {
      return { 'padding-left': `${(row.level - 2) * 24}px` };
    }
  }

  hasCellOptions(rowUuid: string, colIndex: number) {
    const foundItems = this.getCellOptions(rowUuid, colIndex);
    return foundItems && foundItems.length > 0;
  }

  getCellOptions(rowUuid: string, colIndex: number): string[] {
    if (this.rowColumns && this.rowColumns[colIndex]) {
      const targetCol = this.rowColumns[colIndex];
      if (targetCol && targetCol.cellDropdownItems && targetCol.cellDropdownItems[rowUuid]) {
        return targetCol.cellDropdownItems[rowUuid].items;
      }
    }
    return null;
  }

  onCellOptionsClick(row: ProjectChecklistRow, colIndex: number) {
    if (!this.rowColumns || this.rowColumns.length < 1) {
      return;
    }
    const dialogRef = this.dialog.open(EditCellDialogComponent, {
      width: '650px',
      data: {
        cellData: ChecklistCellDataItem.deepCopy(this.rowColumns[colIndex]?.cellDropdownItems[row.uuid]),
        placeholder: row.dataPlaceholder[this.rowColumns[colIndex].name],
        iconData: row.data[this.rowColumns[colIndex].name],
        isIcon: this.rowColumns[colIndex]?.isIcon
      },
      disableClose: true,
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe((data: EditCellDialogResponse) => {
        if (data) {
          // list items
          if (data.updatedCellData != null) {
            if (this.rowColumns && this.rowColumns.length > 0) {
              if (this.rowColumns[colIndex] && this.rowColumns[colIndex].cellDropdownItems) {
                this.rowColumns[colIndex].cellDropdownItems[row.uuid] = data.updatedCellData;
              }
            }
          }
          // placeholder
          row.dataPlaceholder[this.rowColumns[colIndex].name] = data.placeholder;
          // icon data
          if (this.rowColumns[colIndex].isIcon) {
            row.data[this.rowColumns[colIndex].name] = data.iconData;
          }
          this.pageDirty = true;
          this.prepareTableData();
        }
      });
  }

  /**
   * Recursive function to find the parent row containing the given row
   * @param rows list of rows to search for given row in
   * @param orphan row to find parent list
   * @returns true if in top row list, parent row if found, or null if given row is truely orphaned
   */
  private findParentRow(rows: ProjectChecklistRow[], orphan: ProjectChecklistRow): ProjectChecklistRow | boolean {
    if (rows.includes(orphan)) {
      return true;
    } else {
      for (const row of rows) {
        const parentFound = this.findParentRow(row.children, orphan);
        if (parentFound != null) {
          if (parentFound === true) {
            // Return parent row
            return row;
          } else {
            // If we already found the parent, continue passing it up through the recursive calls
            return parentFound;
          }
        }
      }
    }
    return null;
  }

  getMaxLevel() {
    let maxLevel = 0;
    const recFunc = (rows: ProjectChecklistRow[]) => {
      for (const row of rows) {
        if (row.level > maxLevel) {
          maxLevel = row.level;
        }
        if (row.children.length > 0) {
          recFunc(row.children);
        }
      }
    };
    recFunc(this.checklistRow.children);
    return maxLevel;
  }

  // ------               ---------
  // ------ LEVEL CONTROL ---------
  // ------     vvvv      ---------

  // getLevels(level: number, row?: ProjectChecklistRow, includeAll = true) {
  //   const levels = [];
  //   if (includeAll) {
  //     for (let i = level; i > 0; i--) {
  //       levels.push(i);
  //     }
  //   } else {
  //     // Add one level deeper if not first item
  //     const parentRow = this.findParentRow(this.checklistRow.children, row);
  //     if (parentRow == null) {
  //       return [];
  //     }
  //     let index: number;
  //     if (parentRow === true) {
  //       index = this.checklistRow.children.indexOf(row);
  //     } else {
  //       const children = (parentRow as ProjectChecklistRow).children;
  //       index = children.indexOf(row);
  //     }
  //     if (index > 0) {
  //       levels.push(level + 1);
  //     }
  //     levels.push(level);
  //     // Add one level higher if not in top row
  //     if (level > 1) {
  //       levels.push(level - 1);
  //     }
  //   }
  //   return levels.reverse();
  // }

  // onMaxLevelChange() {
  //   this.prepareTableData();
  // }

  // /**
  //  * Update the level of all nested children of the given row
  //  * @param row row to update level of children
  //  * @param changeFactor a positive or negative value to add to the level (e.g. 1, -1)
  //  */
  // private updateChildLevels(row: ProjectChecklistRow, changeFactor: number) {
  //   for (const childRow of row.children) {
  //     childRow.level += changeFactor;
  //     this.updateChildLevels(childRow, changeFactor);
  //   }
  // }

  // /**
  //  * Moves a row up or down a level including any child rows
  //  * @param index index of the row to move
  //  * @param newLevel level to move the row to
  //  */
  // onLevelChange(index: number, newLevel: number) {
  //   const currentRow = this.flattenChecklistRows(this.checklistRow.children)[index];
  //   if (newLevel > currentRow.level) {
  //     // Move row down a level
  //     const parentRow = this.findParentRow(this.checklistRow.children, currentRow);
  //     let newParent: ProjectChecklistRow;
  //     if (parentRow === true) {
  //       const parentIndex = this.checklistRow.children.indexOf(currentRow);
  //       newParent = this.checklistRow.children[parentIndex - 1];
  //       // Remove from current parent
  //       this.checklistRow.children.splice(parentIndex, 1);
  //     } else {
  //       const children = (parentRow as ProjectChecklistRow).children;
  //       const parentIndex = children.indexOf(currentRow);
  //       newParent = children[parentIndex - 1];
  //       // Remove from current parent
  //       children.splice(parentIndex, 1);
  //     }
  //     // Add to new parent
  //     currentRow.level = newLevel;
  //     this.updateChildLevels(currentRow, 1);
  //     newParent.children.push(currentRow);
  //   } else {
  //     // Move row up a level
  //     const parentRow = this.findParentRow(this.checklistRow.children, currentRow) as ProjectChecklistRow;
  //     const grandparentRow = this.findParentRow(this.checklistRow.children, parentRow);
  //     // Add to grandparent
  //     if (grandparentRow === true) {
  //       const grandparentIndex = this.checklistRow.children.indexOf(parentRow);
  //       this.checklistRow.children.splice(grandparentIndex + 1, 0, currentRow);
  //     } else {
  //       const children = (grandparentRow as ProjectChecklistRow).children;
  //       const grandparentIndex = children.indexOf(parentRow);
  //       children.splice(grandparentIndex + 1, 0, currentRow);
  //     }
  //     currentRow.level = newLevel;
  //     this.updateChildLevels(currentRow, -1);
  //     // Remove from parent
  //     const parentIndex = parentRow.children.indexOf(currentRow);
  //     parentRow.children.splice(parentIndex, 1);
  //   }
  //   // Update max shown level if this row would be hidden after level change
  //   if (currentRow.level > this.maxLevelShown) {
  //     this.maxLevelShown = this.getMaxLevel();
  //   }
  //   this.pageDirty = true;
  //   this.prepareTableData();
  // }

  // ------     ^^^^      ---------
  // ------ LEVEL CONTROL ---------
  // ------               ---------


  onAddColumn(index: number) {
    const dialogRef = this.dialog.open(EditColumnDialogComponent, {
      width: '650px',
      data: {
        column: null,
        names: this.rowColumns.map(column => Utils.stringToNameObject(column.name)),
        showLock: true
      },
      disableClose: true,
    });

    dialogRef.afterClosed().toPromise().then(
      (column: ProjectChecklistColumn) => {
        if (column != null) {
          this.rowColumns.splice(index + 1, 0, column);
          this.flattenChecklistRows(this.checklistRow.children).forEach(row => {
            row.data[column.name] = null;
          });
          this.pageDirty = true;
          this.prepareTableData();
        }
      }
    );
  }

  onEditColumn(index: number) {
    const dialogRef = this.dialog.open(EditColumnDialogComponent, {
      width: '650px',
      data: {
        column: ProjectChecklistColumn.deepCopy(this.rowColumns[index]),
        names: this.rowColumns.filter(column => column.name !== this.rowColumns[index].name).map(column => Utils.stringToNameObject(column.name)),
        showLock: false
      },
      disableClose: true,
    });

    dialogRef.afterClosed().toPromise().then(
      (column: ProjectChecklistColumn) => {
        if (column != null) {
          const originalName = this.rowColumns[index].name;
          this.rowColumns.splice(index, 1, column);
          if (originalName !== column.name) {
            this.flattenChecklistRows(this.checklistRow.children).forEach(row => {
              row.data[column.name] = row.data[originalName];
              delete row.data[originalName];
              row.dataPlaceholder[column.name] = row.dataPlaceholder[originalName];
              delete row.dataPlaceholder[originalName];
            });
            if (this.checklistRow.columnSettings.find(col => col.uuid === column.uuid)) {
              this.checklistRow.columnSettings.find(col => col.uuid === column.uuid).name = column.name;
            }
          }
          this.pageDirty = true;
          this.prepareTableData();
        }
      }
    );
  }

  onRemoveColumn(index: number) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.REMOVE_COLUMN_WARNING'),
      }
    });

    dialogRef.afterClosed().toPromise().then((confirm: boolean) => {
      if (confirm) {
        const [columnName] = this.rowColumns.splice(index, 1);
        this.flattenChecklistRows([this.checklistRow]).forEach(row => {
          delete row.data[columnName.name];
        });
        this.pageDirty = true;
        this.prepareTableData();
      }
    });
  }

  onAddRow(index: number) {
    const newRow = new ProjectChecklistRow();
    this.rowColumns.forEach(column => {
      newRow.data[column.name] = null;
    });

    // Special case for adding row at top
    if (index < 0) {
      newRow.level = 2;
      this.checklistRow.children.splice(0, 0, newRow);
      this.maxLevelShown = this.getMaxLevel();
      this.pageDirty = true;
      this.prepareTableData();
      return;
    }

    const currentRow = this.flattenChecklistRows(this.checklistRow.children)[index];
    newRow.level = currentRow.level;

    const parentRow = this.findParentRow(this.checklistRow.children, currentRow);
    if (parentRow === true) {
      this.checklistRow.children.splice(this.checklistRow.children.indexOf(currentRow) + 1, 0, newRow);
    } else {
      const children = (parentRow as ProjectChecklistRow).children;
      children.splice(children.indexOf(currentRow) + 1, 0, newRow);
    }

    this.pageDirty = true;
    this.prepareTableData();
  }

  onEditRow(index: number) {
    const dialogRef = this.dialog.open(EditRowDialogComponent, {
      width: '750px',
      data: {
        row: ProjectChecklistRow.deepCopy(this.flattenChecklistRows(this.checklistRow.children)[index])
      },
      disableClose: true,
    });

    dialogRef.afterClosed().toPromise().then(
      (row: ProjectChecklistRow) => {
        if (row != null) {
          const currentRow = this.flattenChecklistRows(this.checklistRow.children)[index];
          const parentRow = this.findParentRow(this.checklistRow.children, currentRow);
          if (parentRow === true) {
            this.checklistRow.children[this.checklistRow.children.indexOf(currentRow)] = row;
          } else if (parentRow !== null) {
            const children = (parentRow as ProjectChecklistRow).children;
            children[children.indexOf(currentRow)] = row;
          }
          this.prepareTableData();
          this.pageDirty = true;
        }
      }
    );
  }

  private removeRow(row: ProjectChecklistRow) {
    const parentRow = this.findParentRow(this.checklistRow.children, row);
    if (parentRow === true) {
      this.checklistRow.children.splice(this.checklistRow.children.indexOf(row), 1);
    } else {
      const children = (parentRow as ProjectChecklistRow).children;
      children.splice(children.indexOf(row), 1);
    }

    this.pageDirty = true;
    this.prepareTableData();
  }

  onRemoveRow(index: number) {
    const currentRow = this.flattenChecklistRows(this.checklistRow.children)[index];
    let message: string;
    if (currentRow.children.length > 0) {
      message = this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.REMOVE_CHILDREN_WARNING');
    } else {
      message = this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.REMOVE_ROW_WARNING');
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: message,
      }
    });

    dialogRef.afterClosed().toPromise().then((confirm: boolean) => {
      if (confirm) {
        this.removeRow(currentRow);
      }
    });
  }


  onSave() {
    this.pageDirty = false;
    this.saveSectionNotifier.emit({ row: this.checklistRow, columns: this.rowColumns });
    this.toggleEdit()
  }

  onCancel(timeout = 0) {
    this.pageDirty = false;
    this.checklistRow = new ProjectChecklistRow(this.originalRow);
    this.rowColumns = [];
    this.originalCols.forEach(col => this.rowColumns.push(new ProjectChecklistColumn(col)));
    this.cancelNotifier.emit({ row: this.originalRow, columns: this.originalCols, timeout: timeout });
    this.toggleEdit();
  }

  toggleEdit() {
    if (this.editInProgress && !this.editMode) return;
    if (!this.editMode) {
      this.originalRow = new ProjectChecklistRow(this.checklistRow);
      this.originalCols = [];
      this.rowColumns.forEach(col => this.originalCols.push(new ProjectChecklistColumn(col)));
    }
    this.editMode = !this.editMode;
    this.prepareTableData();
    this.editColumn = '';
    this.editRow = -1;
    this.editInProgressChange.emit(this.editMode);
  }

  hasEditAccess(): boolean {
    if (!this.isInitialUser) {
      return true;
    }
    return !this.checklistService.getIsSubmittedProject();
  }

  copySection() {
    let newRow = new ProjectChecklistRow(this.checklistRow);
    this.flattenChecklistRows([newRow]).forEach(row => {
      const newUuid = uuid();
      for (const col of this.rowColumns) {
        if (col.cellDropdownItems && col.cellDropdownItems[row.uuid]) col.cellDropdownItems[newUuid] = col.cellDropdownItems[row.uuid];
      }
      row.uuid = newUuid;
    });
    this.addSectionNotifier.emit({ row: newRow, columns: this.rowColumns });
  }

  removeSection() {
    this.flattenChecklistRows([this.checklistRow]).forEach(row => {
      for (const col of this.rowColumns) {
        if (col.cellDropdownItems && col.cellDropdownItems[row.uuid]) delete col.cellDropdownItems[row.uuid];
      }
    });
    this.removeSectionNotifier.emit(this.rowColumns);
  }

  addFeedstock() {
    let newRow = new ProjectChecklistRow(this.checklistRow);
    this.flattenChecklistRows([newRow]).forEach((row, index) => {
      const newUuid = uuid();
      for (const [i, col] of this.rowColumns.entries()) {
        if (index === 0 && i === 0) {
          continue;
        } else if (this.checklistRow.columnSettings.find(c => c.uuid === col.uuid)) {
          if (!this.checklistRow.columnSettings.find(c => c.uuid === col.uuid).locked) {
            delete row.data[col.name];
          }
        } else if (!col.locked) {
          delete row.data[col.name];
        }
        if (col.cellDropdownItems && col.cellDropdownItems[row.uuid]) col.cellDropdownItems[newUuid] = col.cellDropdownItems[row.uuid];
      }
      row.uuid = newUuid;
    });
    this.addSectionNotifier.emit({ row: newRow, columns: this.rowColumns });
  }

  showDeleteButton() {
    return !this.isFirstFeedstockSection;
  }

  toggleLock(colName) {
    if (this.checklistRow.columnSettings.find(c => c.name === colName)) {
      this.checklistRow.columnSettings.find(c => c.name === colName).locked = !this.checklistRow.columnSettings.find(c => c.name === colName).locked
    } else {
      const newCol = ProjectChecklistColumn.deepCopy(this.rowColumns.find(column => column.name === colName));
      newCol.locked = !newCol.locked;
      this.checklistRow.columnSettings.push(newCol);
    }
    this.loadColumnSettings();
    this.markFormAsDirty();
  }

  isLocked(colName) {
    if (this.checklistRow.columnSettings.find(c => c.name === colName)) {
      return this.checklistRow.columnSettings.find(c => c.name === colName).locked;
    }
    return this.rowColumns.find(c => c.name === colName).locked;
  }

  loadColumnSettings() {
    for (const col of this.rowColumns) {
      let c = this.checklistRow.columnSettings.find(c => c.name === col.name);
      if (c) {
        this.columnLockSettings[col.name] = c.locked;
      } else {
        this.columnLockSettings[col.name] = col.locked;
      }
    }
  }
}

